import { api } from '@/lib/http-client';
import type {
  CreateProductRequest,
  Product,
  ProductFilters,
  ProductListResponse,
  ProductPhotoUpload,
  UpdateProductRequest,
  UploadProductPhotoRequest,
} from '@/types/products';

/**
 * `GET /api/tenants/:tenantId/products` — una PÁGINA del catálogo (24-sep-2026).
 *
 * El servidor pagina (`limite`/`offset`) y filtra (`buscar` en nombre/marca/categoría/descripción,
 * `activo`, `marca` y `categoria` exactas), y devuelve además el `total` que coincide con los filtros y
 * las opciones de marca/categoría de TODO el catálogo (son las opciones de los desplegables del panel).
 * Los filtros vacíos no viajan, así que la URL queda limpia y `queryKeys.products.list(tenantId, filtros)`
 * sigue distinguiendo cada combinación.
 */
export function listProducts(
  tenantId: string,
  filtros: ProductFilters = {},
): Promise<ProductListResponse> {
  return api.get<ProductListResponse>(`/api/tenants/${tenantId}/products`, {
    query: {
      ...(filtros.buscar ? { buscar: filtros.buscar } : {}),
      ...(filtros.activo !== undefined ? { activo: filtros.activo ? 'true' : 'false' } : {}),
      ...(filtros.marca ? { marca: filtros.marca } : {}),
      ...(filtros.categoria ? { categoria: filtros.categoria } : {}),
      ...(filtros.limite !== undefined ? { limite: filtros.limite } : {}),
      ...(filtros.offset ? { offset: filtros.offset } : {}),
    },
  });
}

/** `GET /api/tenants/:tenantId/products/:productId` — 404 si no es de este negocio. */
export function fetchProduct(tenantId: string, productId: string): Promise<Product> {
  return api.get<Product>(`/api/tenants/${tenantId}/products/${productId}`);
}

/** `POST /api/tenants/:tenantId/products` (solo ADMIN) — responde 201 con el producto creado. */
export function createProduct(
  tenantId: string,
  payload: CreateProductRequest,
): Promise<Product> {
  return api.post<Product>(`/api/tenants/${tenantId}/products`, payload);
}

/** `PATCH /api/tenants/:tenantId/products/:productId` (solo ADMIN). */
export function updateProduct(
  tenantId: string,
  productId: string,
  payload: UpdateProductRequest,
): Promise<Product> {
  return api.patch<Product>(`/api/tenants/${tenantId}/products/${productId}`, payload);
}

/** `DELETE /api/tenants/:tenantId/products/:productId` (solo ADMIN). */
export function deleteProduct(
  tenantId: string,
  productId: string,
): Promise<{ deleted: boolean }> {
  return api.delete<{ deleted: boolean }>(`/api/tenants/${tenantId}/products/${productId}`);
}

/**
 * `POST /api/tenants/:tenantId/products/:productId/duplicar` (solo ADMIN) — responde 201 con la COPIA.
 *
 * Copia la estructura (precios, variantes, reglas, FAQs, descripción, marca, categoría e inventario) y
 * deja **vacías las fotos** y los anuncios, y el producto **inactivo**: es un borrador para revisar.
 */
export function duplicateProduct(tenantId: string, productId: string): Promise<Product> {
  return api.post<Product>(`/api/tenants/${tenantId}/products/${productId}/duplicar`, {});
}

/**
 * `POST /api/tenants/:tenantId/products/fotos` (solo ADMIN) — sube UNA foto del equipo.
 *
 * Responde 201 con la URL ya guardada en disco (`GET /uploads/...`), que es lo que hay que meter en
 * `fotos`. El producto no se toca: la foto se asocia cuando se guarda el formulario, así que este
 * endpoint sirve igual para crear (el producto aún no existe) y para editar.
 *
 * El backend acepta hasta 5 MB y detecta el formato por firma binaria (JPEG, PNG, WEBP, GIF o HEIC);
 * si el archivo no es una imagen, responde 400 con el motivo.
 */
export function uploadProductPhoto(
  tenantId: string,
  payload: UploadProductPhotoRequest,
): Promise<ProductPhotoUpload> {
  return api.post<ProductPhotoUpload>(`/api/tenants/${tenantId}/products/fotos`, payload);
}
